import React, { useEffect, useState } from 'react';
import { Form, Table, Space } from 'antd';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { EditOutlined, DeleteOutlined } from '@ant-design/icons';

import CompanySettingsCard from '../CompanySettingsCard';
import HoverHelp from '../../common/HoverHelp';
import SimpleFormModal from '../../common/modals/SimpleFormModal';
import OnTraccrButton from '../../common/buttons/OnTraccrButton';
import OnTraccrTextInput from '../../common/inputs/OnTraccrTextInput';
import OnTraccrCheckbox from '../../common/inputs/OnTraccrCheckbox';
import useToggle from '../../common/hooks/useToggle';
import CustomConfirmModal from '../../common/modals/CustomConfirmModal';
import {
  createABWorkReportingJobCode,
  deleteABWorkReportingJobCode,
  getABWorkReportingJobCodes,
  updateABWorkReportingJobCode,
} from '../../abWorkReporting/state/abWorkReporting.actions';
import BorderlessButton from '../../common/buttons/BorderlessButton';

export default function ABWorkReportingJobCodeSettings({
  disabled = false,
}) {
  const dispatch = useDispatch();
  const jobCodes = useSelector((state) => state.abWorkReporting.jobCodes);
  const [form] = Form.useForm();
  const { toggle, isToggled } = useToggle();
  const [selectedJobCode, setSelectedJobCode] = useState(null);

  useEffect(() => {
    dispatch(getABWorkReportingJobCodes());
  }, [dispatch]);

  useEffect(() => {
    if (!isToggled) {
      setSelectedJobCode(null);
      form.resetFields();
      return;
    }

    form.setFieldsValue({
      name: selectedJobCode?.name ?? '',
      isRoutineWork: !!selectedJobCode?.isRoutineWork,
    });
  }, [isToggled, form, selectedJobCode]);

  const openAddModal = () => {
    setSelectedJobCode(null);
    toggle();
  };

  const openEditModal = (record) => {
    setSelectedJobCode(record);
    toggle();
  };

  const onSave = async () => {
    const values = await form.validateFields();
    const trimmedName = values.name?.trim();
    if (!trimmedName) return;

    const payload = {
      name: trimmedName,
      isRoutineWork: !!values.isRoutineWork,
    };

    const result = selectedJobCode
      ? await dispatch(updateABWorkReportingJobCode(selectedJobCode.name, payload))
      : await dispatch(createABWorkReportingJobCode(payload));
    if (result) toggle();
  };

  const onDelete = async (name) => {
    CustomConfirmModal({
      title: 'Delete Job Code',
      content: 'Are you sure you want to delete this job code?',
      onOk: async () => {
        await dispatch(deleteABWorkReportingJobCode(name));
      },
    });
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Routine Work?',
      dataIndex: 'isRoutineWork',
      key: 'isRoutineWork',
      width: 140,
      render: (isRoutineWork) => (isRoutineWork ? 'Yes' : 'No'),
    },
  ];

  if (!disabled) {
    columns.push({
      title: 'Actions',
      key: 'actions',
      width: 140,
      render: (_, record) => (
        <Space>
          <BorderlessButton
            iconNode={<EditOutlined />}
            onClick={() => openEditModal(record)}
          />
          <BorderlessButton
            iconNode={<DeleteOutlined style={{ color: 'red' }} />}
            onClick={() => onDelete(record.name)}
          />
        </Space>
      ),
    });
  }

  return (
    <CompanySettingsCard
      title="Job Codes for Import"
      leftHeaderView={(
        <>
          <OnTraccrButton
            title="Add"
            type="primary"
            onClick={openAddModal}
            disabled={disabled}
            style={{ marginRight: 10 }}
          />
          <HoverHelp
            content="Configure routine and provisional job codes shown when importing AB work order files."
            placement="top"
            iconProps={{ style: { fontSize: 16 } }}
          />
        </>
      )}
    >
      <Table
        rowKey="name"
        dataSource={jobCodes}
        columns={columns}
        pagination={false}
        size="small"
      />
      <SimpleFormModal
        title={`${selectedJobCode ? 'Edit' : 'Add'} Job Code`}
        visible={isToggled}
        onSave={onSave}
        onClose={toggle}
        width={480}
      >
        <Form form={form} layout="vertical" style={{ width: '100%' }}>
          <Form.Item
            name="name"
            label="Name"
            rules={[{ required: true, message: 'Name is required' }]}
          >
            <OnTraccrTextInput placeholder="Enter job code name" />
          </Form.Item>
          <Form.Item name="isRoutineWork">
            <OnTraccrCheckbox label="Routine Work?" />
          </Form.Item>
        </Form>
      </SimpleFormModal>
    </CompanySettingsCard>
  );
}

ABWorkReportingJobCodeSettings.propTypes = {
  disabled: PropTypes.bool,
};

ABWorkReportingJobCodeSettings.defaultProps = {
  disabled: false,
};
